import { useEffect, useMemo, useState } from 'react';

import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Tour from 'reactour';

import { SubsetSuggestedSubset } from './components/subsets-suggested-subsets';
import { GetSubsetsResponseDto } from '../../../../../../../backend/src/case/subset/dto/get-subsets.response.dto';
import { useMatterProcessingStore } from '../../../../stores/matter-processing-store';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useDeleteSubset } from '@/api/mutations/useDeleteSubset';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetSubsets } from '@/api/queries/useGetSubsets';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { Icon } from '@/new/components/icon';
import { SubsetsListOnboarding } from '@/new/components/onboarding/subsets-list-onboarding';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Button } from '@/new/components/shadcn/button';
import { ScrollableTable } from '@/new/components/table/scrollable-table';
import { SUBSETS_LIST_TOUR_STEPS } from '@/new/constants/tour-steps';
import { useTour } from '@/new/hooks/useTour';
import { formatDatetimeForTable, selectColumn } from '@/new/lib/table-utils';

const fallbackTableData: GetSubsetsResponseDto[] = [];
const columnHelper = createColumnHelper<GetSubsetsResponseDto>();

export function Subsets() {
  const { t } = useTranslation();
  const { matterId } = useParams();
  if (!matterId) return null;

  const { isOpen: isTourOpen, openTour, closeTour } = useTour('subsets-list');

  const [globalFilter, setGlobalFilter] = useState('');
  const [subsetToDelete, setSubsetToDelete] = useState<GetSubsetsResponseDto | null>(null);
  const { mutate: deleteSubset, isLoading: isDeleting } = useDeleteSubset();

  const columns = useMemo(
    () => [
      selectColumn<GetSubsetsResponseDto>(),
      columnHelper.accessor('name', {
        header: () => <span>Subset Name</span>,
        cell: (info) => (
          <div className="flex items-center gap-2">
            <Link to={info.row.original.id}>{info.getValue()}</Link>
            {info.row.original.status === 'processing' && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                <Icon className="lucide--loader-2 h-3 w-3 animate-spin" />
                Processing...
              </span>
            )}
            {info.row.original.status === 'failed' && (
              <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                <Icon className="lucide--alert-circle h-3 w-3" />
                Failed
              </span>
            )}
          </div>
        ),
        meta: {
          sortIcon: 'string',
        },
      }),
      columnHelper.accessor('createdBy.email', {
        header: () => <span>Created By</span>,
        meta: {
          sortIcon: 'string',
        },
      }),
      columnHelper.accessor('createdDate', {
        header: () => <span>Date Created</span>,
        cell: (info) => formatDatetimeForTable(info.getValue()),
      }),
      columnHelper.accessor('docCount', {
        header: () => <span>No. of Docs</span>,
        cell: (info) => {
          const count = info.getValue();
          return count !== undefined ? `${count}` : '-';
        },
        meta: {
          sortIcon: 'number',
        },
      }),
      columnHelper.accessor('factsCount', {
        header: () => <span>No. of Facts</span>,
        cell: (info) => {
          const count = info.getValue();
          return count !== undefined ? `${count}` : '0';
        },
        meta: {
          sortIcon: 'number',
        },
      }),
      columnHelper.display({
        id: 'actions',
        cell: (info) => {
          if (info.row.original.isAllDocuments) return null;
          return (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={(e) => {
                e.stopPropagation();
                setSubsetToDelete(info.row.original);
              }}
            >
              <Icon className="lucide--trash-2 h-4 w-4 text-gray-500 hover:text-red-600" />
            </Button>
          );
        },
      }),
    ],
    [],
  );

  const navigate = useNavigate();
  const { data: matter } = useGetMatterV2(matterId);
  const { data: subsets, isLoading } = useGetSubsets(matterId);

  // Track page view
  useEffect(() => {
    trackCustomEvent(TrackingEventsV2.ViewSubsets, { matterId });
  }, [matterId]);

  // Filter out isAllDocuments case from the table - memoized to prevent unnecessary table recalculations
  const regularCases = useMemo(() => subsets?.filter((c) => !c.isAllDocuments) ?? fallbackTableData, [subsets]);

  const table = useReactTable({
    data: regularCases,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableRowSelection: true,
    initialState: {
      sorting: [
        {
          id: 'name',
          desc: false,
        },
      ],
      pagination: {
        pageSize: 15,
      },
    },
    state: {
      globalFilter,
    },
  });

  const { getProcessingState } = useMatterProcessingStore();

  return (
    <>
      <Breadcrumb
        items={
          matter
            ? [
                { href: '/matters', label: 'Matters', active: false },
                { href: `/matters/${matterId}`, label: matter.name, active: false, iconClass: 'lucide--folder' },
                { href: `/matters/${matterId}/subsets`, label: 'Subsets', active: true },
              ]
            : [{ href: '/matters', label: 'Matters', active: false }]
        }
      />

      <PageContainer>
        <PageHeader className="mb-5">
          <PageTitle
            text="Subsets"
            subtext={`Use Subsets to ${t('analyse')} information from a smaller, focused collection of documents.`}
          />

          {getProcessingState(matterId)?.hasCompletedRuns && (
            <Button variant="primary" asChild data-tour="create-subset-btn">
              <Link to="new" onClick={() => trackCustomEvent(TrackingEventsV2.OpenCreateNewSubset, { matterId })}>
                <Icon className="lucide--group" />
                <span>Create a New Subset</span>
              </Link>
            </Button>
          )}
        </PageHeader>

        <SubsetSuggestedSubset />
      </PageContainer>

      <ScrollableTable
        table={table}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        plural="subsets"
        rowOnClick={(row) => navigate(row.original.id)}
        searchDataTour="subsets-search"
        tableDataTour="subsets-table"
        loading={isLoading}
      />

      <ConfirmationModal
        isOpen={!!subsetToDelete}
        onOpenChange={(open) => !open && setSubsetToDelete(null)}
        title="Delete Subset"
        onConfirm={() => {
          if (subsetToDelete && matterId) {
            deleteSubset(
              { matterId, subsetId: subsetToDelete.id },
              {
                onSuccess: () => setSubsetToDelete(null),
              },
            );
          }
        }}
        isLoading={isDeleting}
      >
        <p>
          Are you sure you want to delete <strong>{subsetToDelete?.name}</strong>? <br /> <br />
          This will remove any chronologies or Kim answers associated with this subset, but not remove documents or
          facts. This action cannot be undone.
        </p>
      </ConfirmationModal>

      <SubsetsListOnboarding onTakeTour={openTour} />

      <Tour startAt={0} steps={SUBSETS_LIST_TOUR_STEPS} isOpen={isTourOpen} onRequestClose={closeTour} />
    </>
  );
}
